import { getCrmColors } from "../../theme/crmColors";
import React, { useState, useEffect } from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import { makeStyles } from '@material-ui/core/styles';
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import ContactNotesDialogListItem from '../ContactNotesDialogListItem';
import ConfirmationModal from '../ConfirmationModal';
import ContactNotesEditModal from '../ContactNotesEditModal';

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import ButtonWithSpinner from '../ButtonWithSpinner';

import useTicketNotes from '../../hooks/useTicketNotes';
import { Grid } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        '& .MuiTextField-root': {
            width: '100%',
        },
    },
    textField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#ffffff" : "#343434"),
            color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc"),
            "& fieldset": {
                borderColor: theme.ticketDark ? getCrmColors(theme, "dark").border : (theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.12)"),
            },
            "&:hover fieldset": {
                borderColor: theme.ticketDark ? getCrmColors(theme, "dark").accent : (theme.mode === "light" ? "#c6d4e6" : "rgba(255,255,255,0.20)"),
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
                boxShadow: theme.mode === "light" ? "0 0 0 3px rgba(93, 170, 252, 0.12)" : "0 0 0 3px rgba(93, 170, 252, 0.14)",
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.ticketDark ? getCrmColors(theme, "dark").muted : (theme.mode === "light" ? "#64748b" : "#cbd5e1"),
        },
    },
    list: {
        width: '100%',
        maxHeight: '200px',
        borderRadius: 8,
        border: theme.ticketDark ? `1px solid ${getCrmColors(theme, "dark").border}` : (theme.mode === "light" ? "1px solid #e6edf6" : "1px solid rgba(255,255,255,0.08)"),
        backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#f8fbff" : "#343434"),
        overflow: 'auto',
        ...theme.scrollbarStyles,
    },
    inline: {
        width: '100%'
    },
    cancelButton: {
        borderRadius: 8,
        fontWeight: 800,
        textTransform: "uppercase",
        color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#64748b" : "#dbeafe"),
        borderColor: theme.ticketDark ? getCrmColors(theme, "dark").border : (theme.mode === "light" ? "#d7e1ee" : "rgba(255,255,255,0.14)"),
    },
    saveButton: {
        borderRadius: 8,
        fontWeight: 800,
        textTransform: "uppercase",
        boxShadow: theme.mode === "light" ? "0 8px 18px rgba(93, 170, 252, 0.16)" : "0 8px 18px rgba(0,0,0,0.18)",
    },
}));

const NoteSchema = Yup.object().shape({
    note: Yup.string()
        .min(2, "Too Short!")
        .required("Required")
});
export function ContactNotes({ ticket }) {
    const { id: ticketId, contactId } = ticket
    const classes = useStyles()
    const [newNote, setNewNote] = useState({ note: "" });
    const [loading, setLoading] = useState(false)
    const [showOnDeleteDialog, setShowOnDeleteDialog] = useState(false)
    const [selectedNote, setSelectedNote] = useState({})
    const [notes, setNotes] = useState([])
    const { saveNote, deleteNote, listNotes } = useTicketNotes()
    const [editingNote, setEditingNote] = useState(null);

    useEffect(() => {
        async function openAndFetchData() {
            handleResetState()
            await loadNotes()
        }
        openAndFetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleResetState = () => {
        setNewNote({ note: "" })
        setLoading(false)
    }

    const handleChangeComment = (e) => {
        setNewNote({ note: e.target.value })
    }

    const handleEdit = (note) => {
        console.log(note)
        setEditingNote(note);
    };

    const handleSave = async values => {
        setLoading(true)
        try {
            await saveNote({
                ...values,
                ticketId,
                contactId
            })
            await loadNotes()
            setNewNote({ note: '' })
            toast.success('Observação adicionada com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const handleOpenDialogDelete = (item) => {
        setSelectedNote(item)
        setShowOnDeleteDialog(true)
    }

    const handleDelete = async () => {
        setLoading(true)
        try {
            await deleteNote(selectedNote.id)
            await loadNotes()
            setSelectedNote({})
            toast.success('Observação excluída com sucesso!')
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }

    const loadNotes = async () => {
        setLoading(true)
        try {
            const notes = await listNotes({ ticketId, contactId })
            setNotes(notes)
        } catch (e) {
            toast.error(e)
        }
        setLoading(false)
    }


    const renderNoteList = () => {
        return notes.map((note) => {
            return <ContactNotesDialogListItem
                note={note}
                key={note.id}
                deleteItem={handleOpenDialogDelete}
                editItem={() => handleEdit(note)}
            />
        })
    }

    return (
        <>
            <ContactNotesEditModal
                open={editingNote !== null}
                onClose={() => setEditingNote(null)}
                note={editingNote ? editingNote.note : ''}
                onSave={handleSave}
            />
            <ConfirmationModal
                title="Excluir Registro"
                open={showOnDeleteDialog}
                onClose={setShowOnDeleteDialog}
                onConfirm={handleDelete}
            >
                Deseja realmente excluir este registro?
            </ConfirmationModal>
            <Formik
                initialValues={newNote}
                enableReinitialize={true}
                validationSchema={NoteSchema}
                onSubmit={(values, actions) => {
                    setTimeout(() => {
                        handleSave(values);
                        actions.setSubmitting(false);
                    }, 400);
                }}
            >

                {({ touched, errors, setErrors }) => (
                    <Form className={classes.root}>
                        <Grid container spacing={2}>
                            <Grid xs={12} item>
                                <Field
                                    as={TextField}
                                    name="note"
                                    rows={3}
                                    label={i18n.t("ticketOptionsMenu.appointmentsModal.textarea")}
                                    placeholder={i18n.t("ticketOptionsMenu.appointmentsModal.placeholder")}
                                    multiline={true}
                                    error={touched.note && Boolean(errors.note)}
                                    helperText={touched.note && errors.note}
                                    variant="outlined"
                                    onChange={handleChangeComment}
                                    fullWidth
                                    className={classes.textField}
                                />
                            </Grid>
                            {notes.length > 0 && (
                                <Grid xs={12} item>
                                    <List className={classes.list}>
                                        {renderNoteList()}
                                    </List>
                                </Grid>
                            )}
                            <Grid xs={12} item>
                                <Grid container spacing={2}>
                                    <Grid xs={6} item>
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                setNewNote({ note: "" });
                                                setErrors({});
                                            }}
                                            color="primary"
                                            variant="outlined"
                                            fullWidth
                                            className={classes.cancelButton}
                                        >
                                            Cancelar
                                        </Button>
                                    </Grid>
                                    <Grid xs={6} item>
                                        <ButtonWithSpinner loading={loading} color="primary" type="submit" variant="contained" autoFocus fullWidth className={classes.saveButton}>
                                            Salvar
                                        </ButtonWithSpinner>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </>
    );
}
